'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { CheckSquare, Clock, AlertCircle, CheckCircle2, Circle, ChevronRight, BarChart2, TrendingUp, TrendingDown, Minus, Ticket, FolderKanban, Calendar, RefreshCw,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type TaskStatus = 'todo' | 'in-progress' | 'review' | 'done';
type TaskPriority = 'critical' | 'high' | 'medium' | 'low';
type TicketStatus = 'open' | 'in-progress' | 'pending' | 'resolved';
type TicketPriority = 'urgent' | 'high' | 'normal' | 'low';

interface Task {
  id: string;
  title: string;
  project: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string;
  assignee: string;
  tags: string[];
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  avatar: string;
  tasksCompleted: number;
  tasksTotal: number;
  ticketsResolved: number;
  avgResponseTime: string;
  trend: 'up' | 'down' | 'flat';
  score: number;
}

interface SupportTicket {
  id: string;
  subject: string;
  client: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: string;
  created: string;
  lastUpdate: string;
  assignee: string;
}

interface WIPProject {
  id: string;
  name: string;
  client: string;
  progress: number;
  status: 'on-track' | 'at-risk' | 'delayed';
  dueDate: string;
  team: string[];
  phase: string;
  tasksLeft: number;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const myTasks: Task[] = [
  { id: 't1', title: 'Finalize API integration for client dashboard', project: 'NovaTech Portal', status: 'in-progress', priority: 'critical', dueDate: 'Today', assignee: 'Ryan A.', tags: ['Backend', 'API'] },
  { id: 't2', title: 'Review and approve BPO agent scripts', project: 'Meridian BPO', status: 'review', priority: 'high', dueDate: 'Tomorrow', assignee: 'Ryan A.', tags: ['BPO', 'QA'] },
  { id: 't3', title: 'Update VOIP routing configuration', project: 'Gulf Retail VOIP', status: 'todo', priority: 'medium', dueDate: 'Aug 15', assignee: 'Ryan A.', tags: ['VOIP', 'Config'] },
  { id: 't4', title: 'Prepare monthly performance report slides', project: 'Internal', status: 'in-progress', priority: 'medium', dueDate: 'Aug 16', assignee: 'Ryan A.', tags: ['Reporting'] },
  { id: 't5', title: 'SEO audit for Apex Digital campaign', project: 'Apex Digital', status: 'todo', priority: 'high', dueDate: 'Aug 18', assignee: 'Ryan A.', tags: ['SEO', 'Marketing'] },
  { id: 't6', title: 'Deploy staging environment for Pinnacle site', project: 'Pinnacle Logistics', status: 'done', priority: 'low', dueDate: 'Aug 10', assignee: 'Ryan A.', tags: ['DevOps'] },
  { id: 't7', title: 'Client onboarding call — Synapse Analytics', project: 'Synapse Analytics', status: 'done', priority: 'high', dueDate: 'Aug 11', assignee: 'Ryan A.', tags: ['Client', 'Onboarding'] },
];

const teamMembers: TeamMember[] = [
  { id: 'm1', name: 'Ryan Ashford', role: 'Senior Dev', avatar: 'RA', tasksCompleted: 18, tasksTotal: 22, ticketsResolved: 14, avgResponseTime: '1.2h', trend: 'up', score: 94 },
  { id: 'm2', name: 'Layla Khalid', role: 'BPO Lead', avatar: 'LK', tasksCompleted: 21, tasksTotal: 25, ticketsResolved: 31, avgResponseTime: '0.8h', trend: 'up', score: 97 },
  { id: 'm3', name: 'Omar Saeed', role: 'Marketing Exec', avatar: 'OS', tasksCompleted: 12, tasksTotal: 18, ticketsResolved: 8, avgResponseTime: '2.4h', trend: 'flat', score: 78 },
  { id: 'm4', name: 'Nadia Hassan', role: 'VOIP Engineer', avatar: 'NH', tasksCompleted: 9, tasksTotal: 14, ticketsResolved: 11, avgResponseTime: '1.8h', trend: 'down', score: 71 },
  { id: 'm5', name: 'Tariq Mansoor', role: 'Web Developer', avatar: 'TM', tasksCompleted: 15, tasksTotal: 17, ticketsResolved: 6, avgResponseTime: '3.1h', trend: 'up', score: 88 },
];

const ticketQueue: SupportTicket[] = [
  { id: 'tk1', subject: 'VOIP calls dropping after 3 minutes', client: 'Gulf Retail Group', status: 'in-progress', priority: 'urgent', category: 'VOIP', created: '2h ago', lastUpdate: '30m ago', assignee: 'Ryan A.' },
  { id: 'tk2', subject: 'Dashboard login not working for 2 users', client: 'NovaTech Solutions', status: 'open', priority: 'high', category: 'IT Support', created: '4h ago', lastUpdate: '4h ago', assignee: 'Ryan A.' },
  { id: 'tk3', subject: 'Invoice #INV-2847 shows wrong amount', client: 'Apex Digital', status: 'pending', priority: 'normal', category: 'Billing', created: '1d ago', lastUpdate: '5h ago', assignee: 'Layla K.' },
  { id: 'tk4', subject: 'BPO agent script needs update for Q3', client: 'Meridian BPO', status: 'open', priority: 'normal', category: 'BPO', created: '1d ago', lastUpdate: '1d ago', assignee: 'Ryan A.' },
  { id: 'tk5', subject: 'SEO report not reflecting latest changes', client: 'Vantage Media', status: 'in-progress', priority: 'low', category: 'Marketing', created: '2d ago', lastUpdate: '6h ago', assignee: 'Omar S.' },
  { id: 'tk6', subject: 'SSL certificate renewal required', client: 'Pinnacle Logistics', status: 'open', priority: 'urgent', category: 'IT Support', created: '3h ago', lastUpdate: '3h ago', assignee: 'Tariq M.' },
];

const wipProjects: WIPProject[] = [
  { id: 'p1', name: 'NovaTech Client Portal', client: 'NovaTech Solutions', progress: 72, status: 'on-track', dueDate: 'Aug 28', team: ['RA', 'TM'], phase: 'Development', tasksLeft: 8 },
  { id: 'p2', name: 'Meridian BPO Expansion', client: 'Meridian BPO', progress: 45, status: 'at-risk', dueDate: 'Aug 20', team: ['LK', 'OS'], phase: 'Training', tasksLeft: 14 },
  { id: 'p3', name: 'Gulf Retail VOIP Migration', client: 'Gulf Retail Group', progress: 88, status: 'on-track', dueDate: 'Aug 16', team: ['NH', 'RA'], phase: 'Testing', tasksLeft: 3 },
  { id: 'p4', name: 'Apex Digital Q3 Campaign', client: 'Apex Digital', progress: 31, status: 'delayed', dueDate: 'Aug 14', team: ['OS', 'LK'], phase: 'Content Creation', tasksLeft: 21 },
  { id: 'p5', name: 'Pinnacle Logistics Website', client: 'Pinnacle Logistics', progress: 60, status: 'on-track', dueDate: 'Sep 5', team: ['TM', 'RA'], phase: 'Design Review', tasksLeft: 11 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const taskStatusConfig: Record<TaskStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  'todo':        { label: 'To Do',       color: 'text-muted-foreground', bg: 'bg-muted/60',      icon: Circle },
  'in-progress': { label: 'In Progress', color: 'text-blue-400',         bg: 'bg-blue-500/10',   icon: RefreshCw },
  'review':      { label: 'In Review',   color: 'text-amber-400',        bg: 'bg-amber-500/10',  icon: AlertCircle },
  'done':        { label: 'Done',        color: 'text-success',          bg: 'bg-success/10',    icon: CheckCircle2 },
};

const taskPriorityConfig: Record<TaskPriority, { label: string; color: string; dot: string }> = {
  'critical': { label: 'Critical', color: 'text-danger',           dot: 'bg-danger' },
  'high':     { label: 'High',     color: 'text-orange-400',       dot: 'bg-orange-400' },
  'medium':   { label: 'Medium',   color: 'text-amber-400',        dot: 'bg-amber-400' },
  'low':      { label: 'Low',      color: 'text-muted-foreground', dot: 'bg-muted-foreground' },
};

const ticketStatusConfig: Record<TicketStatus, { label: string; color: string; bg: string }> = {
  'open':        { label: 'Open',        color: 'text-blue-400',   bg: 'bg-blue-500/10' },
  'in-progress': { label: 'In Progress', color: 'text-amber-400',  bg: 'bg-amber-500/10' },
  'pending':     { label: 'Pending',     color: 'text-purple-400', bg: 'bg-purple-500/10' },
  'resolved':    { label: 'Resolved',    color: 'text-success',    bg: 'bg-success/10' },
};

const ticketPriorityConfig: Record<TicketPriority, { label: string; color: string; bg: string }> = {
  'urgent': { label: 'Urgent', color: 'text-danger',           bg: 'bg-danger/10' },
  'high':   { label: 'High',   color: 'text-orange-400',       bg: 'bg-orange-400/10' },
  'normal': { label: 'Normal', color: 'text-muted-foreground', bg: 'bg-muted/60' },
  'low':    { label: 'Low',    color: 'text-muted-foreground', bg: 'bg-muted/40' },
};

const wipStatusConfig: Record<WIPProject['status'], { label: string; color: string; bg: string; bar: string }> = {
  'on-track': { label: 'On Track', color: 'text-success',      bg: 'bg-success/10',      bar: 'bg-success' },
  'at-risk':  { label: 'At Risk',  color: 'text-amber-400',    bg: 'bg-amber-500/10',    bar: 'bg-amber-400' },
  'delayed':  { label: 'Delayed',  color: 'text-danger',       bg: 'bg-danger/10',       bar: 'bg-danger' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, sub, color }: { icon: React.ElementType; label: string; value: string | number; sub: string; color: string }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 flex items-start gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground leading-none mb-1">{value}</p>
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function EmployeePortalPage() {
  const [taskFilter, setTaskFilter] = useState<TaskStatus | 'all'>('all');
  const [ticketFilter, setTicketFilter] = useState<TicketPriority | 'all'>('all');

  const filteredTasks = taskFilter === 'all' ? myTasks : myTasks.filter((t) => t.status === taskFilter);
  const filteredTickets = ticketFilter === 'all' ? ticketQueue : ticketQueue.filter((t) => t.priority === ticketFilter);

  const tasksDone = myTasks.filter((t) => t.status === 'done').length;
  const tasksInProgress = myTasks.filter((t) => t.status === 'in-progress').length;
  const openTickets = ticketQueue.filter((t) => t.status === 'open' || t.status === 'in-progress').length;
  const urgentTickets = ticketQueue.filter((t) => t.priority === 'urgent').length;

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-6 py-6 max-w-screen-2xl mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-xs font-bold text-primary">RA</span>
              </div>
              <span className="text-sm text-muted-foreground">Good morning,</span>
              <span className="text-sm font-semibold text-foreground">Ryan Ashford</span>
            </div>
            <h1 className="text-2xl font-bold text-foreground">Employee Workspace</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Your operational view — tasks, tickets, team, and active projects</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-card border border-border rounded-lg px-3 py-2">
            <Calendar size={13} />
            <span>Wed, Aug 13, 2026</span>
          </div>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard icon={CheckSquare}  label="Tasks Completed"   value={`${tasksDone}/${myTasks.length}`}  sub="This sprint"          color="bg-success/10 text-success" />
          <KpiCard icon={RefreshCw}    label="In Progress"       value={tasksInProgress}                   sub="Active right now"      color="bg-blue-500/10 text-blue-400" />
          <KpiCard icon={Ticket}       label="Open Tickets"      value={openTickets}                       sub={`${urgentTickets} urgent`} color="bg-amber-500/10 text-amber-400" />
          <KpiCard icon={FolderKanban} label="Active Projects"   value={wipProjects.filter(p => p.status !== 'delayed').length} sub={`${wipProjects.filter(p=>p.status==='delayed').length} delayed`} color="bg-purple-500/10 text-purple-400" />
        </div>

        {/* Main Grid: Tasks + Tickets */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-5">

          {/* ── Assigned Tasks ── */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <CheckSquare size={16} className="text-primary" />
                <h2 className="font-semibold text-foreground text-sm">My Assigned Tasks</h2>
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">{myTasks.length}</span>
              </div>
              <div className="flex items-center gap-1">
                {(['all', 'in-progress', 'review', 'todo', 'done'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setTaskFilter(f)}
                    className={`text-2xs px-2 py-1 rounded-md font-medium transition-colors capitalize ${
                      taskFilter === f ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {f === 'all' ? 'All' : f === 'in-progress' ? 'Active' : f === 'todo' ? 'Todo' : f === 'review' ? 'Review' : 'Done'}
                  </button>
                ))}
              </div>
            </div>
            <div className="divide-y divide-border max-h-[420px] overflow-y-auto scrollbar-thin">
              {filteredTasks.map((task) => {
                const statusCfg = taskStatusConfig[task.status];
                const priorityCfg = taskPriorityConfig[task.priority];
                const StatusIcon = statusCfg.icon;
                return (
                  <div key={task.id} className="px-5 py-3.5 hover:bg-muted/30 transition-colors group">
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 p-1 rounded-md ${statusCfg.bg}`}>
                        <StatusIcon size={13} className={statusCfg.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium leading-snug ${task.status === 'done' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                            {task.title}
                          </p>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className={`w-1.5 h-1.5 rounded-full ${priorityCfg.dot}`} />
                            <span className={`text-2xs font-medium ${priorityCfg.color}`}>{priorityCfg.label}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-2xs text-muted-foreground">{task.project}</span>
                          <span className="text-2xs text-muted-foreground flex items-center gap-1">
                            <Clock size={10} /> {task.dueDate}
                          </span>
                          <div className="flex gap-1 ml-auto">
                            {task.tags.map((tag) => (
                              <span key={tag} className="text-2xs bg-muted/60 text-muted-foreground px-1.5 py-0.5 rounded">{tag}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Ticket Queue ── */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Ticket size={16} className="text-amber-400" />
                <h2 className="font-semibold text-foreground text-sm">Ticket Queue</h2>
                <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded-full font-medium">{ticketQueue.length} open</span>
              </div>
              <div className="flex items-center gap-1">
                {(['all', 'urgent', 'high', 'normal'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setTicketFilter(f)}
                    className={`text-2xs px-2 py-1 rounded-md font-medium transition-colors capitalize ${
                      ticketFilter === f ? 'bg-amber-500 text-white' : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {f === 'all' ? 'All' : f}
                  </button>
                ))}
              </div>
            </div>
            <div className="divide-y divide-border max-h-[420px] overflow-y-auto scrollbar-thin">
              {filteredTickets.map((ticket) => {
                const statusCfg = ticketStatusConfig[ticket.status];
                const priorityCfg = ticketPriorityConfig[ticket.priority];
                return (
                  <div key={ticket.id} className="px-5 py-3.5 hover:bg-muted/30 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xs font-mono text-muted-foreground">#{ticket.id.toUpperCase()}</span>
                          <span className={`text-2xs font-semibold px-1.5 py-0.5 rounded ${priorityCfg.bg} ${priorityCfg.color}`}>{priorityCfg.label}</span>
                          <span className={`text-2xs font-medium px-1.5 py-0.5 rounded ${statusCfg.bg} ${statusCfg.color}`}>{statusCfg.label}</span>
                        </div>
                        <p className="text-sm font-medium text-foreground leading-snug">{ticket.subject}</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-2xs text-muted-foreground">{ticket.client}</span>
                          <span className="text-2xs text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">{ticket.category}</span>
                          <span className="text-2xs text-muted-foreground ml-auto flex items-center gap-1">
                            <Clock size={10} /> {ticket.created}
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-2xs font-bold text-primary">{ticket.assignee.split(' ')[0][0]}{ticket.assignee.split(' ')[1]?.[0] ?? ''}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Grid: WIP Projects + Team Performance */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">

          {/* ── Work-in-Progress Projects ── */}
          <div className="xl:col-span-3 bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <FolderKanban size={16} className="text-purple-400" />
                <h2 className="font-semibold text-foreground text-sm">Work in Progress</h2>
                <span className="text-xs bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded-full font-medium">{wipProjects.length} projects</span>
              </div>
              <button className="text-xs text-primary hover:underline flex items-center gap-1">
                View All <ChevronRight size={12} />
              </button>
            </div>
            <div className="divide-y divide-border">
              {wipProjects.map((project) => {
                const statusCfg = wipStatusConfig[project.status];
                return (
                  <div key={project.id} className="px-5 py-4 hover:bg-muted/20 transition-colors">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-semibold text-foreground truncate">{project.name}</p>
                          <span className={`text-2xs font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${statusCfg.bg} ${statusCfg.color}`}>{statusCfg.label}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-2xs text-muted-foreground">{project.client}</span>
                          <span className="text-2xs text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">{project.phase}</span>
                          <span className="text-2xs text-muted-foreground flex items-center gap-1">
                            <Calendar size={10} /> {project.dueDate}
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-foreground leading-none">{project.progress}%</p>
                        <p className="text-2xs text-muted-foreground">{project.tasksLeft} tasks left</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${statusCfg.bar}`}
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                      <div className="flex -space-x-1.5">
                        {project.team.map((initials) => (
                          <div key={initials} className="w-5 h-5 rounded-full bg-primary/20 border border-card flex items-center justify-center">
                            <span className="text-2xs font-bold text-primary">{initials}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Team Performance ── */}
          <div className="xl:col-span-2 bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <BarChart2 size={16} className="text-success" />
                <h2 className="font-semibold text-foreground text-sm">Team Performance</h2>
              </div>
              <span className="text-2xs text-muted-foreground">This sprint</span>
            </div>
            <div className="divide-y divide-border">
              {teamMembers.map((member) => {
                const completionPct = Math.round((member.tasksCompleted / member.tasksTotal) * 100);
                const TrendIcon = member.trend === 'up' ? TrendingUp : member.trend === 'down' ? TrendingDown : Minus;
                const trendColor = member.trend === 'up' ? 'text-success' : member.trend === 'down' ? 'text-danger' : 'text-muted-foreground';
                const scoreColor = member.score >= 90 ? 'text-success' : member.score >= 75 ? 'text-amber-400' : 'text-danger';
                const scoreBg = member.score >= 90 ? 'bg-success/10' : member.score >= 75 ? 'bg-amber-500/10' : 'bg-danger/10';
                return (
                  <div key={member.id} className="px-5 py-3.5 hover:bg-muted/20 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-primary">{member.avatar}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-foreground">{member.name}</p>
                          <div className="flex items-center gap-1.5">
                            <TrendIcon size={12} className={trendColor} />
                            <span className={`text-sm font-bold ${scoreColor} ${scoreBg} px-1.5 py-0.5 rounded`}>{member.score}</span>
                          </div>
                        </div>
                        <p className="text-2xs text-muted-foreground">{member.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{ width: `${completionPct}%` }}
                        />
                      </div>
                      <span className="text-2xs text-muted-foreground w-14 text-right">{member.tasksCompleted}/{member.tasksTotal} tasks</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-2xs text-muted-foreground flex items-center gap-1">
                        <CheckCircle2 size={10} className="text-success" /> {member.ticketsResolved} resolved
                      </span>
                      <span className="text-2xs text-muted-foreground flex items-center gap-1">
                        <Clock size={10} /> {member.avgResponseTime} avg
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </AppLayout>
  );
}
